import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  animate,
  state,
  style,
  transition,
  trigger
} from "@angular/animations";
import { FilterControlComponent } from "../../elements/filter-control/filter-control.component";
import {
  filterTornOptions,
  filterUnknownOptions
} from "../../../services/device-container-state-controller.service";
import { FilterStatus } from "../../../models/filters";
import { Range } from "../../../models/range-status-days";
import { FilterKey } from "../../../services/filters.service";
import { MatIcon } from "@angular/material/icon";
import { MatAccordion } from "@angular/material/expansion";
import { SvgIconComponent } from "angular-svg-icon";
import { TranslateModule } from "@ngx-translate/core";

/**
 * The four filters the sidenav shows as a row that drills into its own view
 * rather than as an accordion panel that expands in place.
 *
 * They are exactly the server-paged lists: long enough that a searchable list
 * needs the whole sidebar height, where the six beside them are short static
 * option sets that read fine inline. The values are `filter-control` labels, so
 * a row and the control its view mounts name the same filter.
 */
export type DrilldownLabel =
  "containerType" | "incident" | "lastAuthorizedSite" | "device";

/** Icon (registered in `icon-registry.service.ts`) per drill-down row. */
const DRILLDOWN_ICONS: Record<DrilldownLabel, string> = {
  containerType: "container",
  incident: "incident",
  lastAuthorizedSite: "last-site",
  device: "device"
};

@Component({
  selector: "app-sidenav-filters",
  templateUrl: "./sidenav-filters.component.html",
  styleUrls: ["./sidenav-filters.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger("slideInOut", [
      state(
        "in",
        style({
          transform: "translate3d(0, 0, 0)"
        })
      ),
      state(
        "out",
        style({
          transform: "translate3d(-100%, 0, 0)" // ← négatif pour partir à gauche
        })
      ),
      transition("in => out", animate("300ms ease-in-out")),
      transition("out => in", animate("300ms ease-in-out"))
    ])
  ],
  imports: [
    MatIcon,
    MatAccordion,
    SvgIconComponent,
    FilterControlComponent,
    TranslateModule
  ]
})
export class SidenavFiltersComponent {
  @Input() isOpen: boolean = false;
  @Output() torn: EventEmitter<string> = new EventEmitter();
  @Output() unknown: EventEmitter<string> = new EventEmitter();
  @Output() devices: EventEmitter<string[]> = new EventEmitter();
  @Output() lastSites: EventEmitter<string[]> = new EventEmitter();
  @Output() incidents: EventEmitter<FilterKey[]> = new EventEmitter();
  @Output() containers: EventEmitter<string[]> = new EventEmitter();
  @Output() filter: EventEmitter<FilterStatus> = new EventEmitter();
  @Output() statusDays: EventEmitter<Range | undefined> = new EventEmitter();
  @Output() site: EventEmitter<string | undefined> = new EventEmitter();
  @Output() confidenceRadius: EventEmitter<string[]> = new EventEmitter();
  @Output() devicesTypes: EventEmitter<string[]> = new EventEmitter();
  @Output() containerCategory: EventEmitter<string[]> = new EventEmitter();
  @Output() addIncidentDevices: EventEmitter<Number[]> = new EventEmitter();
  @Output() toggleSidenav: EventEmitter<boolean> = new EventEmitter();
  @Output() colorStatus: EventEmitter<FilterStatus[]> = new EventEmitter();

  @Input() filterTorn: string;
  @Input() filterUnknown: string;
  @Input() filterStatusDays: Range;
  @Input() filterDevices: string[] = [];
  @Input() filterLastSites: string[] = [];
  @Input() filterIncidents: FilterKey[] = [];
  @Input() filterContainers: string[] = [];
  @Input() filterConfidenceRadius: string[];
  @Input() filterDevicesTypes: string[];
  @Input() filterContainerCategory: string[] = [];
  @Input() filterStatus: FilterStatus[] = [];

  public tornFilterOptions: string[] = Object.keys(filterTornOptions);
  public unknownFilterOptions: string[] = Object.keys(filterUnknownOptions);

  // Access to filter panel components
  @ViewChildren(FilterControlComponent)
  filterPanels!: QueryList<FilterControlComponent>;

  /**
   * Which drill-down filter has the sidebar body, or `null` for the filter list.
   *
   * This is the whole of the drill-down: the view it opens mounts the shared
   * `filter-control` in `detail` chrome, which fetches, searches, pages and
   * emits on its own. The sidenav deliberately keeps no list, no pagination and
   * no staged selection of its own — carrying a second copy of all that is what
   * 9a783310 removed.
   */
  public currentDetailView: DrilldownLabel | null = null;

  // ─── Sidenav toggle ──────────────────────────────────────────────────────────

  public toggle(): void {
    // Closing commits what is still staged, like Apply does: the X button and the
    // overlay are ordinary ways to leave the panel, and a typed range used to be
    // dropped on the way out because it is the only filter that waits for an
    // apply signal. Guarded on isOpen so opening applies nothing.
    if (this.isOpen) {
      this.applyStagedPanels();
      // Leave on the filter list, so reopening does not resume a drill-down the
      // user has already walked away from.
      this.closeDetailView();
    }
    this.toggleSidenav.emit(!this.isOpen);
  }

  // ─── Drill-down view ─────────────────────────────────────────────────────────

  public openDetailView(label: DrilldownLabel): void {
    this.currentDetailView = label;
  }

  public closeDetailView(): void {
    this.currentDetailView = null;
  }

  public detailIcon(label: DrilldownLabel): string {
    return DRILLDOWN_ICONS[label];
  }

  /** Badge beside the drill-down title, and on the row it was opened from. */
  public detailCount(label: DrilldownLabel): number {
    switch (label) {
      case "containerType":
        return this.filterContainers.length;
      case "incident":
        return this.filterIncidents.length;
      case "lastAuthorizedSite":
        return this.filterLastSites.length;
      case "device":
        return this.filterDevices.length;
    }
  }

  /**
   * Clear All inside a drill-down clears that one filter, where the same button
   * on the filter list clears all of them. The mounted control owns the
   * selection, so ask it rather than reaching for the `@Input` behind it.
   */
  public clearDetailFilter(): void {
    this.filterPanels?.find((panel) => panel.chrome === "detail")?.clearItems();
  }

  /**
   * Commit whatever the panels still hold un-emitted. Every filter type emits as
   * soon as it changes except `range`/`dateRange`, which only set `callApply` and
   * wait — so those are the ones a close (or an Apply click) has to flush.
   * `applyItems()` is unforced, so an untouched panel does not re-emit.
   */
  private applyStagedPanels(): void {
    this.filterPanels?.forEach((panel) => {
      if (panel.type === "range" || panel.type === "dateRange") {
        panel.applyItems();
      }
    });
  }

  // ─── Filter panel event handlers ─────────────────────────────────────────────

  public manageTorn(torn: string): void {
    this.torn.emit(torn);
  }

  public manageUnknown(unknown: string): void {
    this.unknown.emit(unknown);
  }

  public manageDevicesTypes(devicesTypes: string[]): void {
    this.devicesTypes.emit(devicesTypes);
  }

  public manageContainerCategory(categories: string[]): void {
    this.containerCategory.emit(categories);
  }

  public manageLastSites(sites: string[]): void {
    this.lastSites.emit(sites);
  }

  public manageContainers(containers: string[]): void {
    this.containers.emit(containers);
  }

  public manageDevices(devices: string[]): void {
    this.devices.emit(devices);
  }

  public managecolorStatus(colors: FilterStatus[]): void {
    this.colorStatus.emit(colors);
  }

  /**
   * Emits the colour selection, never an empty list. filterStatus mirrors the
   * store, so it can still be empty when the user acts early; emitting that
   * would persist "[]" and leave the colour filters with no rows to render
   * (see readColorStatusSelection).
   */
  private emitColorStatus(): void {
    if (this.filterStatus?.length) {
      this.colorStatus.emit(this.filterStatus);
    }
  }

  public manageConfidenceRadius(radius: string[]): void {
    this.confidenceRadius.emit(radius);
  }

  public manageStatusDays(days: Range): void {
    this.statusDays.emit(days);
  }

  public manageIncidents(incidents: FilterKey[]): void {
    this.incidents.emit(incidents);
  }

  // ─── Main view actions ────────────────────────────────────────────────────────

  public clearAllFilters(): void {
    this.filterDevices = [];
    this.filterLastSites = [];
    this.filterIncidents = [];
    this.filterContainers = [];

    // Uncheck every status (empty selection = no filter = show all).
    this.filterStatus = this.filterStatus.map((filter) => ({
      ...filter,
      show: false
    }));

    this.filterConfidenceRadius = [];
    this.filterDevicesTypes = [];
    this.filterContainerCategory = [];
    this.filterStatusDays = undefined;

    if (this.filterPanels) {
      this.filterPanels.forEach((panel) => {
        if (
          panel.label !== "torn" &&
          panel.label !== "unknown" &&
          panel.label !== "colorStatus"
        ) {
          panel.clearItems();
        }
      });
    }

    this.devices.emit(this.filterDevices);
    this.lastSites.emit(this.filterLastSites);
    this.incidents.emit(this.filterIncidents);
    this.containers.emit(this.filterContainers);
    this.emitColorStatus();
    this.confidenceRadius.emit(this.filterConfidenceRadius);
    this.devicesTypes.emit(this.filterDevicesTypes);
    this.containerCategory.emit(this.filterContainerCategory);
    this.torn.emit(this.filterTorn);
    this.unknown.emit(this.filterUnknown);
    this.statusDays.emit(this.filterStatusDays);
  }

  public applyAllFilters(): void {
    if (this.filterDevices && this.filterDevices.length > 0) {
      this.devices.emit(this.filterDevices);
    }
    if (this.filterLastSites && this.filterLastSites.length > 0) {
      this.lastSites.emit(this.filterLastSites);
    }
    if (this.filterIncidents && this.filterIncidents.length > 0) {
      this.incidents.emit(this.filterIncidents);
    }
    if (this.filterContainers && this.filterContainers.length > 0) {
      this.containers.emit(this.filterContainers);
    }
    this.emitColorStatus();
    if (this.filterConfidenceRadius && this.filterConfidenceRadius.length > 0) {
      this.confidenceRadius.emit(this.filterConfidenceRadius);
    }
    if (this.filterDevicesTypes && this.filterDevicesTypes.length > 0) {
      this.devicesTypes.emit(this.filterDevicesTypes);
    }
    if (
      this.filterContainerCategory &&
      this.filterContainerCategory.length > 0
    ) {
      this.containerCategory.emit(this.filterContainerCategory);
    }
    // The two radios always hold a value, so no guard: emit them as they are.
    this.torn.emit(this.filterTorn);
    this.unknown.emit(this.filterUnknown);
    // Let the range panel emit its own value rather than reading filterStatusDays
    // back: this @Input only tracks what the user typed while it still shares its
    // object with the page, and a Clear All (which sets it to undefined) detaches
    // it — after that, reading it here published a stale value or nothing at all.
    // A half-open range is passed through as typed.
    this.applyStagedPanels();

    this.toggle();
  }
}
